"use client";

import { useId, type ComponentProps, type ReactNode } from "react";
import { cn } from "../lib/utils";
import "./patterns.css";

export type SettingsLayoutProps = Omit<ComponentProps<"div">, "title"> & {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  navigation?: ReactNode;
};

export function SettingsLayout({
  title,
  description,
  actions,
  navigation,
  children,
  className,
  ...props
}: SettingsLayoutProps) {
  return (
    <div {...props} className={cn("ks-settings-layout", className)}>
      {navigation ? <div className="mb-6 text-[13px] text-fg-muted">{navigation}</div> : null}
      <header className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 basis-48">
          <h1 className="text-2xl font-semibold leading-8 tracking-[-0.025em] text-fg [overflow-wrap:anywhere]">
            {title}
          </h1>
          {description ? (
            <div className="mt-2 text-[13px] leading-6 text-fg-muted">{description}</div>
          ) : null}
        </div>
        {actions ? (
          <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </header>
      <div className="space-y-9">{children}</div>
    </div>
  );
}

type DataAttributes = { [key: `data-${string}`]: string | number | boolean | undefined };
type DivSlot = ComponentProps<"div"> & DataAttributes;

export type SettingsSectionProps = Omit<ComponentProps<"section">, "title"> & {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  unstyled?: boolean;
  slots?: { header?: "header" | "div"; description?: "div" | "p" };
  slotProps?: {
    header?: DivSlot;
    heading?: DivSlot;
    title?: ComponentProps<"h2">;
    description?: DivSlot;
    actions?: DivSlot;
  };
};

export function SettingsSection({
  title,
  description,
  actions,
  children,
  className,
  unstyled = false,
  slots,
  slotProps = {},
  ...props
}: SettingsSectionProps) {
  const id = useId();
  const Header = slots?.header ?? "header";
  const Description = slots?.description ?? "div";
  return (
    <section {...props} aria-labelledby={id} className={cn(!unstyled && "min-w-0", className)}>
      <Header
        {...slotProps.header}
        className={cn(
          !unstyled && "mb-3 flex flex-wrap items-start justify-between gap-3 px-1",
          slotProps.header?.className,
        )}
      >
        <div
          {...slotProps.heading}
          className={cn(!unstyled && "min-w-0 flex-1", slotProps.heading?.className)}
        >
          <h2
            {...slotProps.title}
            id={id}
            className={cn(!unstyled && "text-sm font-medium text-fg", slotProps.title?.className)}
          >
            {title}
          </h2>
          {description ? (
            <Description
              {...slotProps.description}
              className={cn(
                !unstyled && "mt-1 text-[13px] leading-5 text-fg-muted",
                slotProps.description?.className,
              )}
            >
              {description}
            </Description>
          ) : null}
        </div>
        {actions ? (
          <div
            {...slotProps.actions}
            className={cn(!unstyled && "flex flex-wrap gap-2", slotProps.actions?.className)}
          >
            {actions}
          </div>
        ) : null}
      </Header>
      {children}
    </section>
  );
}

export function SettingsGroup({
  className,
  unstyled = false,
  ...props
}: ComponentProps<"div"> & { unstyled?: boolean }) {
  return (
    <div
      {...props}
      className={cn(
        !unstyled &&
          "ks-settings-group divide-y divide-line rounded-panel border border-line bg-panel px-4 shadow-panel",
        className,
      )}
    />
  );
}

export type SettingsRowProps = Omit<ComponentProps<"div">, "title"> &
  DataAttributes & {
    title: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    iconPresentation?: "glyph" | "avatar";
    actions?: ReactNode;
    actionLayout?: "inline" | "stacked";
    unstyled?: boolean;
    slotProps?: {
      icon?: ComponentProps<"span">;
      content?: DivSlot;
      title?: DivSlot;
      description?: DivSlot;
      actions?: DivSlot;
    };
  };

export function SettingsRow({
  title,
  description,
  icon,
  iconPresentation = "glyph",
  actions,
  actionLayout = "inline",
  children,
  className,
  unstyled = false,
  slotProps = {},
  ...props
}: SettingsRowProps) {
  return (
    <div
      {...props}
      data-has-icon={Boolean(icon)}
      data-action-layout={actionLayout}
      className={cn(!unstyled && "ks-settings-row", className)}
    >
      {icon ? (
        <span
          {...slotProps.icon}
          className={cn(
            !unstyled && "grid size-8 shrink-0 place-items-center",
            !unstyled && iconPresentation === "glyph" && "rounded-control bg-hover text-fg-muted",
            slotProps.icon?.className,
          )}
        >
          {icon}
        </span>
      ) : null}
      <div
        {...slotProps.content}
        className={cn(!unstyled && "ks-settings-description min-w-0", slotProps.content?.className)}
      >
        <div
          {...slotProps.title}
          className={cn(!unstyled && "text-[13px] font-medium text-fg", slotProps.title?.className)}
        >
          {title}
        </div>
        {description ? (
          <div
            {...slotProps.description}
            className={cn(
              !unstyled && "mt-0.5 text-xs leading-5 text-fg-muted [overflow-wrap:anywhere]",
              slotProps.description?.className,
            )}
          >
            {description}
          </div>
        ) : null}
        {children}
      </div>
      {actions ? (
        <div
          {...slotProps.actions}
          className={cn(
            !unstyled && "ks-settings-actions flex flex-wrap items-center justify-end gap-2",
            slotProps.actions?.className,
          )}
        >
          {actions}
        </div>
      ) : null}
    </div>
  );
}
